import { useCallback } from 'react';
import { useFormDataContext, useRestApiPostRequest } from '~/hooks';
import {
    ExploreYourOptionsInputParams,
    ExploreYourOptionsPot,
    ExploreYourOptionsResponse,
    IncomeType,
    PolicyValuation,
} from '~/types';

type ExploreOptionsParams = Partial<ExploreYourOptionsInputParams> & {
    [key: string]: string | number | undefined;
};

export const useExploreOptions = () => {
    const formData = useFormDataContext();
    const { customerDetails, calculations, finalSalary, statePension, otherIncome, targetIncome, otherPensionPots } =
        formData;
    const { retirementAge } = customerDetails.personalInformation;

    const { data, loading, sendRequest } = useRestApiPostRequest<
        ExploreYourOptionsResponse,
        Partial<ExploreYourOptionsInputParams>
    >({
        path: '/explore-your-options',
        options: { withCredentials: true },
    });

    const buildParams = useCallback(
        (customParams?: Partial<ExploreOptionsParams>) => {
            const finalSalaryStartAge = finalSalary.amount ? Number(finalSalary.payableAge) : undefined;
            const otherIncomeStartAge = otherIncome.amount ? Number(otherIncome.startAge) : undefined;
            const otherPotValue = otherPensionPots?.pensionAmount ?? 0;
            const otherContributions = otherPensionPots?.grossContributions ?? 0;
            const policies: PolicyValuation[] = customerDetails?.policies || [];

            return {
                potValue: policies?.reduce((sum, policy) => sum + (policy.policyValuation || 0), 0),
                slContributions: customerDetails?.contributions || 0,
                otherPotValue: Number(otherPotValue),
                otherContributions: Number(otherContributions),
                dateOfBirth: customerDetails.personalInformation.dateOfBirth,
                targetIncome: Number(targetIncome.amount ?? 0),
                retirementAge,
                additionalContributions: 0,
                tfcPercentage: 25,
                remainingPotValue: calculations?.remainingPotValue ?? 0,
                statePension: Number(statePension.amount ?? 0),
                finalSalary: Number(finalSalary.amount ?? 0),
                otherIncome: Number(otherIncome.amount ?? 0),
                finalSalaryStartAge,
                otherIncomeStartAge,
                ...customParams,
            };
        },
        [
            calculations?.projectedPotValue,
            calculations?.remainingPotValue,
            customerDetails.personalInformation.dateOfBirth,
            finalSalary.amount,
            finalSalary.payableAge,
            otherIncome.amount,
            otherIncome.startAge,
            statePension.amount,
            targetIncome.amount,
        ]
    );

    const findIncomeAmount = useCallback((pots: ExploreYourOptionsPot[], type: string) => {
        const targetPot = pots?.find(pot => pot.startAge >= retirementAge);
        return targetPot?.incomes?.find(income => income.type === type)?.amount ?? 0;
    }, []);

    const processResponse = useCallback(
        (response: ExploreYourOptionsResponse) => {
            if (!response?.payload) return null;

            return {
                annuityCost: response.payload.annuityCost ?? 0,
                annuityIncomePerYear: findIncomeAmount(response.payload.pots, IncomeType.ANNUITY),
                drawdownPotUsed: response.payload.drawdownPotUsed ?? 0,
                drawdownIncomePerYear: findIncomeAmount(response.payload.pots, IncomeType.DRAWDOWN),
                remainingPotValue: response.payload.remainingPotValue ?? 0,
                ...response.payload,
            };
        },
        [findIncomeAmount]
    );

    const calculateOptions = useCallback(
        async (customParams?: Partial<ExploreOptionsParams>) => {
            const params = buildParams(customParams);
            const response = await sendRequest(params);
            if (response) {
                return processResponse(response);
            }
        },
        [buildParams, sendRequest, processResponse]
    );

    return {
        calculateOptions,
        loading,
        currentData: data ? processResponse(data) : null,
    };
};
